/**
 * Upload in die Social-Mediathek — direkt vom Browser in den Speicher.
 *
 * Ablauf: Standbild im Browser erzeugen (Foto verkleinert, Video bei etwa 25 %)
 * → Engine legt das Medium an und gibt zwei signierte Upload-Links → Datei und
 * Standbild per PUT → Engine prueft und stoesst die Beschreibung an.
 *
 * Das Standbild entsteht hier und nicht spaeter: Ohne Standbild ist ein Video von
 * der Beschreibung ausgeschlossen und hat nirgends eine Vorschau (Konzept 10).
 */

export const ERLAUBT = ["image/jpeg", "image/png", "image/webp", "video/mp4", "video/quicktime", "video/webm"];

export interface Vorschau {
  blob: Blob | null;
  width: number | null;
  height: number | null;
  duration_s: number | null;
}

const MAX_KANTE = 1280;

function mitFrist<T>(versprechen: Promise<T>, ms: number, text: string): Promise<T> {
  return new Promise<T>((ok, fehler) => {
    const uhr = setTimeout(() => fehler(new Error(text)), ms);
    versprechen.then(
      (wert) => { clearTimeout(uhr); ok(wert); },
      (e: unknown) => { clearTimeout(uhr); fehler(e); },
    );
  });
}

function aufLeinwand(quelle: CanvasImageSource, w: number, h: number): Promise<Blob | null> {
  if (!w || !h) return Promise.resolve(null);
  const f = Math.min(1, MAX_KANTE / Math.max(w, h));
  const leinwand = document.createElement("canvas");
  leinwand.width = Math.max(1, Math.round(w * f));
  leinwand.height = Math.max(1, Math.round(h * f));
  const ctx = leinwand.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(quelle, 0, 0, leinwand.width, leinwand.height);
  return new Promise((ok) => leinwand.toBlob((b) => ok(b), "image/jpeg", 0.82));
}

/** Standbild und Masse — scheitert leise (dann ohne Standbild), nie der ganze Upload. */
export async function vorschauErzeugen(datei: File): Promise<Vorschau> {
  const leer: Vorschau = { blob: null, width: null, height: null, duration_s: null };
  const url = URL.createObjectURL(datei);
  try {
    if (datei.type.startsWith("image/")) {
      const bild = new Image();
      bild.decoding = "async";
      bild.src = url;
      await mitFrist(bild.decode(), 15_000, "Foto nicht lesbar");
      return {
        blob: await aufLeinwand(bild, bild.naturalWidth, bild.naturalHeight),
        width: bild.naturalWidth || null, height: bild.naturalHeight || null, duration_s: null,
      };
    }
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.src = url;
    await mitFrist(new Promise<void>((ok, fehler) => {
      video.onloadedmetadata = () => ok();
      video.onerror = () => fehler(new Error("Video nicht lesbar"));
    }), 15_000, "Video nicht lesbar");
    const dauer = Number.isFinite(video.duration) ? video.duration : 0;
    await mitFrist(new Promise<void>((ok, fehler) => {
      video.onseeked = () => ok();
      video.onerror = () => fehler(new Error("Standbild nicht lesbar"));
      video.currentTime = dauer > 0.5 ? dauer * 0.25 : 0.1;
    }), 15_000, "Standbild nicht lesbar");
    return {
      blob: await aufLeinwand(video, video.videoWidth, video.videoHeight),
      width: video.videoWidth || null, height: video.videoHeight || null, duration_s: dauer || null,
    };
  } catch {
    return leer;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** PUT auf einen signierten Link, mit Fortschritt. Der Content-Type muss zur Signatur passen. */
export function hochladen(url: string, daten: Blob, typ: string, fortschritt?: (anteil: number) => void): Promise<void> {
  return new Promise((ok, fehler) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", typ);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && fortschritt) fortschritt(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? ok()
      : fehler(new Error(`Upload fehlgeschlagen (${xhr.status})`)));
    xhr.onerror = () => fehler(new Error("Upload fehlgeschlagen — Verbindung unterbrochen"));
    xhr.send(daten);
  });
}
